import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

const keyFor = (userId?: string | null) => `enviroeng.sidebar.open:${userId ?? "anon"}`;

/**
 * Per-user sidebar open/closed state. Persists to localStorage keyed on the
 * signed-in user's id so different accounts on the same device keep their own
 * preference. Falls back to open when nothing is stored.
 */
export function useSidebarPersistence() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [open, setOpenState] = useState(true);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let next = true;
    try {
      const stored = localStorage.getItem(keyFor(userId));
      if (stored !== null) next = stored === "true";
    } catch {
      /* ignore */
    }
    setOpenState(next);
    setHydrated(true);
  }, [userId]);

  const setOpen = useCallback(
    (value: boolean) => {
      setOpenState(value);
      try {
        localStorage.setItem(keyFor(userId), String(value));
      } catch {
        /* ignore */
      }
    },
    [userId],
  );

  return { open, setOpen, hydrated };
}
